import { useState, useEffect } from 'react';
import axios from 'axios';
import { Loader2, MessageSquare, AlertTriangle, Users } from 'lucide-react';

const API = '/api';

function fmtDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return iso; }
}

const FIELD_LABEL = {
  fitter_note: 'Note for their fitter',
  improve_text: 'Anything we could do better',
  why_not_text: 'What would change their mind',
  strokes_dropped: 'Strokes dropped',
};

export default function SurveyResults() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/survey/results`);
        setData(res.data);
      } catch (e) {
        setError(e.response?.data?.detail || 'Could not load survey results.');
      }
      setLoading(false);
    })();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="surveyresults-loading">
        <Loader2 className="animate-spin text-hg-green" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-400/30 text-red-300 rounded-sm px-4 py-3 text-sm font-sans" data-testid="surveyresults-error">
        {error}
      </div>
    );
  }

  const c = data?.counts || {};
  const verbatims = data?.verbatims || [];

  return (
    <div className="space-y-6" data-testid="surveyresults-page">
      <div>
        <h1 className="font-display text-2xl lg:text-3xl text-hg-text tracking-wider">Survey Results</h1>
        <p className="text-sm text-hg-text-dim font-sans mt-1">
          Every answer customers have given, as a whole rather than one inbox item at a time.
        </p>
      </div>

      {/* ---- headline funnel */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="surveyresults-counts">
        {[
          ['Responses', c.responses],
          ['Tokens minted', c.invites_total],
          ['Opened the form', c.opened_total],
          ['Abandoned after opening', c.abandoned_after_opening],
          ['Resubmitted', c.resubmitted],
        ].map(([label, v]) => (
          <div key={label} className="bg-hg-surface border border-hg-border rounded-sm p-3">
            <div className="text-[10px] font-sans uppercase tracking-wider text-hg-text-dim">{label}</div>
            <div className="font-display text-2xl text-hg-text">{v ?? '—'}</div>
          </div>
        ))}
      </div>

      {/* ---- which form each response answered. Shown up front because it governs how every
              number below may be read. */}
      {(data?.by_form_version || []).length > 0 && (
        <div className="flex flex-wrap gap-2 items-center text-xs font-sans" data-testid="surveyresults-versions">
          <span className="text-hg-text-dim uppercase tracking-wider text-[10px]">Form version</span>
          {data.by_form_version.map((v) => (
            <span
              key={String(v.form_version)}
              className={`rounded-sm px-2 py-1 border ${
                v.form_version === null || v.form_version === 'unknown'
                  ? 'border-red-400/50 text-red-300 bg-red-400/10'
                  : 'border-hg-border text-hg-text-dim'
              }`}
            >
              {v.form_version === null ? 'unstamped' : `v${v.form_version}`}: {v.responses}
            </span>
          ))}
        </div>
      )}

      {/* ---- by source */}
      <div className="bg-hg-surface border border-hg-border rounded-sm p-4" data-testid="surveyresults-funnel">
        <h2 className="font-display text-lg text-hg-text tracking-wide mb-1">By channel</h2>
        <p className="text-xs font-sans text-hg-text-dim mb-3">{data?.invites_are_tokens_note}</p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm font-sans">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-hg-text-dim border-b border-hg-border">
                <th className="text-left py-2">Channel</th>
                <th className="text-right py-2">Tokens</th>
                <th className="text-right py-2">Opened</th>
                <th className="text-right py-2">Submitted</th>
                <th className="text-right py-2">Submit rate</th>
                <th className="text-right py-2">Abandoned</th>
              </tr>
            </thead>
            <tbody>
              {(data?.funnel || []).map((f) => (
                <tr key={f.source} className="border-b border-hg-border/50">
                  <td className="py-2 text-hg-text">{f.source}</td>
                  <td className="py-2 text-right text-hg-text-dim">{f.invites}</td>
                  <td className="py-2 text-right text-hg-text-dim">{f.opened}</td>
                  <td className="py-2 text-right text-hg-text">{f.submitted}</td>
                  <td className="py-2 text-right text-hg-green">{f.submit_rate === null ? '—' : `${f.submit_rate}%`}</td>
                  <td className="py-2 text-right text-hg-text-dim">{f.abandoned_after_opening}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ---- FREE TEXT FIRST. It is the most valuable thing on the form, so it is not
              buried under the charts and it is never truncated. */}
      <div data-testid="surveyresults-verbatims">
        <h2 className="font-display text-lg text-hg-text tracking-wide mb-1 flex items-center gap-2">
          <MessageSquare size={17} className="text-hg-green" />
          In their own words ({verbatims.length})
        </h2>
        <p className="text-xs font-sans text-hg-text-dim mb-3">
          Every free-text answer, in full. Nothing summarised, nothing cut.
        </p>
        {verbatims.length === 0 ? (
          <div className="bg-hg-surface border border-hg-border rounded-sm p-8 text-center" data-testid="surveyresults-noverbatims">
            <p className="text-hg-text-dim font-sans text-sm">
              No free-text answers yet. That is a finding, not an empty state — the form is being
              submitted with every comment box left blank.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {verbatims.map((v) => (
              <div key={v.response_id} className="bg-hg-surface border border-hg-border rounded-sm p-4" data-testid="verbatim-card">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-display text-base text-hg-text tracking-wide">{v.name || '(no name)'}</span>
                  <span className="text-xs font-sans text-hg-text-dim">{v.email}</span>
                  <span className="text-[10px] font-sans uppercase tracking-wider rounded-sm px-1.5 py-0.5 border border-hg-border text-hg-text-dim">
                    {v.source}
                  </span>
                  {v.resubmitted && (
                    <span className="text-[10px] font-sans uppercase tracking-wider rounded-sm px-1.5 py-0.5 border border-amber-400/50 text-amber-300" data-testid="verbatim-resubmitted">
                      v{v.version} — updated
                    </span>
                  )}
                  <span className="text-xs font-sans text-hg-text-dim ml-auto">{fmtDate(v.submitted_at)}</span>
                </div>
                <div className="mt-1 text-[11px] font-sans text-hg-text-dim">
                  clubs {v.clubs_performing || '—'} · fitter {v.fitter_rating || '—'} · recommend {v.recommend || '—'}
                  {v.fitter_name ? ` · ${v.fitter_name}` : ''}
                </div>
                <div className="mt-3 space-y-3">
                  {Object.entries(v.text).map(([field, text]) => (
                    <div key={field}>
                      <div className="text-[10px] font-sans uppercase tracking-wider text-hg-text-dim mb-1">
                        {FIELD_LABEL[field] || field}
                      </div>
                      <p className="text-sm text-hg-text font-sans whitespace-pre-wrap leading-relaxed border-l-2 border-hg-green/40 pl-3">
                        {text}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ---- NPS: individual scores only, never a computed net score */}
      {data?.nps && (
        <div className="bg-hg-surface border border-hg-border rounded-sm p-4" data-testid="surveyresults-nps">
          <h2 className="font-display text-lg text-hg-text tracking-wide mb-1">
            Likelihood to recommend (0–10)
          </h2>
          <p className="text-xs font-sans text-hg-text-dim mb-3">{data.nps.note}</p>
          {data.nps.answered === 0 ? (
            <p className="text-sm font-sans text-amber-300/90" data-testid="surveyresults-nps-empty">
              Asked of {data.nps.asked_of}, answered by {data.nps.answered}. The 0–10 scale
              arrived with survey v3 — earlier responses were never asked it.
            </p>
          ) : (
            <>
              <div className="flex items-end gap-1">
                {data.nps.distribution.map((s) => (
                  <div key={s.score} className="flex-1 text-center">
                    <div className="bg-hg-green rounded-sm mx-auto w-full"
                         style={{ height: `${4 + s.n * 18}px` }} />
                    <div className="text-[10px] font-sans text-hg-text-dim mt-1">{s.score}</div>
                  </div>
                ))}
              </div>
              <div className="mt-2 text-xs font-sans text-hg-text-dim">
                {data.nps.answered} answered · {data.nps.detractors} scored 0–6 (worth a call)
                · {data.nps.promoters} scored 9–10
              </div>
            </>
          )}
        </div>
      )}

      {/* ---- distributions */}
      <div data-testid="surveyresults-distributions">
        <h2 className="font-display text-lg text-hg-text tracking-wide mb-1">Per question</h2>
        <p className="text-xs font-sans text-hg-text-dim mb-3">{data?.version_note}</p>
        <div className="grid md:grid-cols-2 gap-3">
          {(data?.distributions || []).map((d) => (
            <div key={d.key} className="bg-hg-surface border border-hg-border rounded-sm p-4">
              <div className="text-sm font-sans text-hg-text mb-1">
                {d.label}
                {d.retired && (
                  <span className="ml-2 text-[10px] font-sans uppercase tracking-wider rounded-sm px-1.5 py-0.5 border border-hg-border text-hg-text-dim">
                    retired
                  </span>
                )}
              </div>
              <div className="text-[11px] font-sans text-hg-text-dim mb-2">
                asked of {d.asked_of} {d.asked_of === 1 ? 'person' : 'people'}
              </div>
              <div className="space-y-1.5">
                {d.options.map((o) => (
                  <div key={o.option} className="flex items-center gap-2">
                    <div className="text-xs font-sans text-hg-text-dim w-40 shrink-0 truncate">{o.option}</div>
                    <div className="flex-1 bg-hg-bg rounded-sm h-3 overflow-hidden">
                      <div className="bg-hg-green h-full" style={{ width: `${o.pct}%` }} />
                    </div>
                    <div className="text-xs font-sans text-hg-text w-16 text-right">{o.n} ({o.pct}%)</div>
                  </div>
                ))}
              </div>
              {/* NOT ASKED is reported separately from BLANK and is excluded from the base
                  above — pooling the two is what made 40% of this data look like refusals. */}
              {d.not_asked > 0 && (
                <div className="mt-2 text-[11px] font-sans text-blue-300/90" data-testid={`not-asked-${d.key}`}>
                  Not asked of {d.not_asked} — {d.retired
                    ? 'retired, so later forms no longer ask it'
                    : `this question arrived in form v${d.first_asked_in_version}`}.
                  Excluded from the percentages above.
                </div>
              )}
              {d.blank > 0 && (
                <div className="mt-1 text-[11px] font-sans text-amber-300/90">
                  Asked but skipped by {d.blank} of {d.asked_of} ({d.blank_pct}%)
                </div>
              )}
              {d.unknown_version > 0 && (
                <div className="mt-1 text-[11px] font-sans text-red-300" data-testid={`unknown-version-${d.key}`}>
                  {d.unknown_version} response(s) have an undetermined form version and are
                  excluded entirely.
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* ---- handicap, with the caveat attached */}
      <div className="bg-hg-surface border border-hg-border rounded-sm p-4" data-testid="surveyresults-handicap">
        <h2 className="font-display text-lg text-hg-text tracking-wide mb-1">Handicap change</h2>
        <div className="flex items-start gap-2 mb-3">
          <AlertTriangle size={15} className="text-amber-400 shrink-0 mt-0.5" />
          <p className="text-xs font-sans text-amber-300/90">{data?.handicap_note}</p>
        </div>
        {(data?.handicap_pairs || []).length === 0 ? (
          <p className="text-sm font-sans text-hg-text-dim">No usable before/after pairs.</p>
        ) : (
          <div className="space-y-1.5">
            {data.handicap_pairs.map((p) => (
              <div key={p.email} className="flex items-center gap-3 text-sm font-sans">
                <span className="text-hg-text w-28 truncate">{p.name || p.email}</span>
                <span className="text-hg-text-dim">{p.before} → {p.after}</span>
                <span className="text-hg-green font-bold">−{p.delta}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ---- per fitter. An empty table here must not read as "no problems". */}
      <div className="bg-hg-surface border border-hg-border rounded-sm p-4" data-testid="surveyresults-byfitter">
        <h2 className="font-display text-lg text-hg-text tracking-wide mb-1 flex items-center gap-2">
          <Users size={17} className="text-hg-green" /> By fitter
        </h2>
        {(data?.by_fitter || []).length === 0 ? (
          <div className="flex items-start gap-2 mt-2" data-testid="surveyresults-nofitter">
            <AlertTriangle size={15} className="text-amber-400 shrink-0 mt-0.5" />
            <p className="text-xs font-sans text-amber-300/90">
              {data?.fitter_attribution?.responses_attributed} of {data?.fitter_attribution?.responses_total} responses
              carry a fitter. {data?.fitter_attribution?.note}
            </p>
          </div>
        ) : (
          <div className="space-y-1.5 mt-2">
            {data.by_fitter.map((g) => (
              <div key={g.fitter_name} className="flex items-center gap-3 text-sm font-sans">
                <span className="text-hg-text w-40 truncate">{g.fitter_name}</span>
                <span className="text-hg-text-dim">{g.responses} responses</span>
                <span className="text-hg-text-dim">{g.excellent} excellent</span>
                <span className="text-hg-text-dim">{g.recommend_yes} would recommend</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
